import React, { useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { locationHubService } from '../../services/locationHubService.js';
import { useTranslation } from 'react-i18next';
import { formatLocationDropdownLabel, sortLocationsAsc } from '../../utils/locationDropdownLabel.js';


const getHubId = (loc) => loc.id;
const getHubLabel = (loc) => formatLocationDropdownLabel(loc) || '—';

export default function VehicleLocationPointFields({
  pickupLocationIds,
  setPickupLocationIds,
  dropLocationIds,
  setDropLocationIds,
}) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [searchInputs, setSearchInputs] = useState({ pickup: '', drop: '' });
  const pickupRef = useRef(null);
  const dropRef = useRef(null);

  useEffect(() => {
    locationHubService
      .getAllHubs(1, 500, { all: 'true' })
      .then((res) => {
        const data = res.data?.hubs || res.data?.data || res.data || [];
        setLocations(Array.isArray(data) ? data : []);
      })
      .catch(() => setLocations([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        pickupRef.current && !pickupRef.current.contains(event.target) &&
        dropRef.current && !dropRef.current.contains(event.target)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getFilteredLocations = (searchTerm, excludeIds = []) => {
    const term = (searchTerm || '').toLowerCase();
    return sortLocationsAsc(locations.filter((loc) => {
      const locId = getHubId(loc);
      if (excludeIds.includes(locId)) return false;
      const label = getHubLabel(loc).toLowerCase();
      const code = (loc.location_code || '').toLowerCase();
      const hubId = (loc.hub_id || '').toLowerCase();
      return label.includes(term) || code.includes(term) || hubId.includes(term);
    }));
  };

  const getSelectableIds = (filteredLocations) =>
    filteredLocations.map((loc) => getHubId(loc));

  const handleToggle = (field, locationId) => {
    const isPickup = field === 'pickup';
    const currentIds = isPickup ? pickupLocationIds : dropLocationIds;
    const setCurrent = isPickup ? setPickupLocationIds : setDropLocationIds;

    if (currentIds.includes(locationId)) {
      setCurrent(currentIds.filter((id) => id !== locationId));
      return;
    }

    setCurrent([...currentIds, locationId]);
  };

  const handleSelectAllFiltered = (field, filteredLocations) => {
    const isPickup = field === 'pickup';
    const currentIds = isPickup ? pickupLocationIds : dropLocationIds;
    const setCurrent = isPickup ? setPickupLocationIds : setDropLocationIds;
    const selectableIds = getSelectableIds(filteredLocations);

    if (selectableIds.length === 0) return;

    const allFilteredSelected = selectableIds.every((id) => currentIds.includes(id));
    if (allFilteredSelected) {
      setCurrent(currentIds.filter((id) => !selectableIds.includes(id)));
      return;
    }

    const merged = [...new Set([...currentIds, ...selectableIds])];
    setCurrent(merged);
  };

  const handleClearSelection = (field) => {
    const isPickup = field === 'pickup';
    const setCurrent = isPickup ? setPickupLocationIds : setDropLocationIds;
    setCurrent([]);
  };

  const handleClearSearch = (field) => {
    setSearchInputs((prev) => ({ ...prev, [field]: '' }));
  };

  const renderDropdown = (field, label, selectedIds, excludeIds, containerRef) => {
    const isOpen = openDropdown === field;
    const searchTerm = searchInputs[field];
    const filteredLocations = getFilteredLocations(searchTerm, excludeIds);
    const selectableIds = getSelectableIds(filteredLocations);
    const allFilteredSelected =
      selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

    return (
      <div className="relative" ref={containerRef}>
        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
          {label}
        </label>
        <div
          onClick={() => !loading && setOpenDropdown(isOpen ? null : field)}
          className={`cursor-pointer border border-gray-300 rounded-xl px-3 sm:px-4 py-2 bg-white flex justify-between items-center text-sm sm:text-base ${
            loading ? 'opacity-60 cursor-not-allowed' : ''
          }`}
        >
          <span className="text-gray-700 truncate">
            {loading
              ? 'Loading locations...'
              : selectedIds.length > 0
                ? `${selectedIds.length} selected`
                : 'Select locations'}
          </span>
          <span className={`text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`}>▾</span>
        </div>

        {isOpen && (
          <div
            className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-xl shadow-lg p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search locations..."
                value={searchTerm}
                onChange={(e) =>
                  setSearchInputs((prev) => ({ ...prev, [field]: e.target.value }))
                }
                autoFocus
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
              />
            </div>

            <div className="flex items-center justify-between gap-2 mb-2 px-1">
              <button
                type="button"
                onClick={() => handleSelectAllFiltered(field, filteredLocations)}
                disabled={selectableIds.length === 0}
                className="text-xs sm:text-sm font-medium text-buttonBg hover:text-orange-700 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                {allFilteredSelected ? 'Deselect all' : 'Select all'}
                {searchTerm ? ' (filtered)' : ''}
              </button>
              <div className="flex items-center gap-3">
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => handleClearSearch(field)}
                    className="text-xs sm:text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear search
                  </button>
                )}
                {selectedIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleClearSelection(field)}
                    className="text-xs sm:text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear selected
                  </button>
                )}
              </div>
            </div>

            <div className="max-h-48 overflow-y-auto border-t border-gray-100 pt-1">
              {filteredLocations.length > 0 ? (
                filteredLocations.map((loc) => {
                  const locId = getHubId(loc);
                  return (
                    <label
                      key={locId}
                      className="flex items-center p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(locId)}
                        onChange={() => handleToggle(field, locId)}
                        className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-blue-400"
                      />
                      <span className="ml-3 text-sm text-gray-800">
                        {getHubLabel(loc)}
                      </span>
                    </label>
                  );
                })
              ) : (
                <p className="text-sm text-gray-500 p-2">
                  {searchTerm ? 'No locations match your search' : 'No locations available'}
                </p>
              )}
            </div>
          </div>
        )}

        <p className="text-xs text-gray-500 mt-1">
          Selected: {selectedIds.length}
          {selectedIds.length > 0 && (
            <span className="block text-gray-600 mt-0.5 truncate">
              {selectedIds
                .map((id) => {
                  const hub = locations.find((loc) => getHubId(loc) === id);
                  return hub ? getHubLabel(hub) : null;
                })
                .filter(Boolean)
                .join(', ')}
            </span>
          )}
        </p>
      </div>
    );
  };

  return (
    <div className="mt-6">
      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
        Route Points
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-6">
        {renderDropdown('pickup', 'Pickup Point', pickupLocationIds, [], pickupRef)}
        {renderDropdown('drop', 'Drop Point', dropLocationIds, [], dropRef)}
      </div>
    </div>
  );
}
